import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast, POSITION } from 'vue-toastification'
import { STORAGE_KEYS, DEFAULT_LOCALE } from '@/utils/constants'
import i18n from '@/config/i18n'
import router from '@/router'
import { applyDocumentTitle } from '@/utils/documentTitle'

function syncToastLocale(locale: string) {
  const toast = useToast()
  const isArabic = locale === 'ar'
  toast.updateDefaults({
    rtl: isArabic,
    position: isArabic ? POSITION.TOP_LEFT : POSITION.TOP_RIGHT,
  })
}

export const useLocaleStore = defineStore('locale', () => {
  // State
  const currentLocale = ref(DEFAULT_LOCALE)
  const availableLocales = ref([
    { code: 'ar', name: 'العربية', direction: 'rtl' },
    { code: 'en', name: 'English', direction: 'ltr' }
  ])

  // Getters
  const isRTL = computed(() => currentLocale.value === 'ar')
  const direction = computed(() => isRTL.value ? 'rtl' : 'ltr')
  const isArabic = computed(() => currentLocale.value === 'ar')
  const isEnglish = computed(() => currentLocale.value === 'en')
  const currentLocaleData = computed(() =>
    availableLocales.value.find(locale => locale.code === currentLocale.value)
  )

  // Actions
  const initializeLocale = () => {
    const savedLocale = localStorage.getItem(STORAGE_KEYS.LOCALE)
    if (savedLocale && ['ar', 'en'].includes(savedLocale)) {
      setLocale(savedLocale)
    } else {
      // Set default and update document
      document.documentElement.dir = direction.value
      document.documentElement.lang = currentLocale.value
      i18n.global.locale.value = currentLocale.value as any
      syncToastLocale(currentLocale.value)
    }
  }

  const setLocale = (locale: string) => {
    if (availableLocales.value.find(l => l.code === locale)) {
      currentLocale.value = locale
      localStorage.setItem(STORAGE_KEYS.LOCALE, locale)

      // Update document direction
      document.documentElement.dir = direction.value
      document.documentElement.lang = locale
      
      // Update i18n locale
      i18n.global.locale.value = locale as any
      syncToastLocale(locale)
      applyDocumentTitle(router.currentRoute.value)
    }
  }

  const toggleLocale = () => {
    const newLocale = currentLocale.value === 'ar' ? 'en' : 'ar'
    setLocale(newLocale)
  }

  return {
    // State
    currentLocale,
    availableLocales,

    // Getters
    isRTL,
    direction,
    isArabic,
    isEnglish,
    currentLocaleData,

    // Actions
    initializeLocale,
    setLocale,
    toggleLocale
  }
})